import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Polygon, useMapEvents, LayersControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useMunicipiosAutocomplete } from '../hooks/useMunicipios';
import { supabase } from '../supabaseClient';
import { normalizarUf } from '../sensoriamento/territorialGeometry';
import { fitMapBoundsSafely, setMapViewSafely, stopMapMotion } from '../utils/leafletMapSafety';
import { formatMapCoordinates, parseMapCoordinates } from '../utils/mapCoordinates';
import './MapSelectModal.css';

const locationIcon = L.divIcon({
  className: 'map-select-location-icon',
  html: '<span aria-hidden="true">📍</span>',
  iconSize: [38, 46],
  iconAnchor: [19, 42],
});

function FlyToLocation({ lat, lon, zoom = 10 }) {
  const map = useMap();

  useEffect(() => {
    if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lon))) return;
    setMapViewSafely(map, [Number(lat), Number(lon)], zoom);
    return () => stopMapMotion(map);
  }, [lat, lon, zoom, map]);

  return null;
}

function FitBoundary({ polygons }) {
  const map = useMap();

  useEffect(() => {
    const points = (polygons || []).flat();
    if (!points.length) return undefined;
    fitMapBoundsSafely(map, points, { padding: [42, 42], maxZoom: 18 });
    return () => stopMapMotion(map);
  }, [map, polygons]);

  return null;
}

function LocationMarker({ initialPosition, onSelect }) {
  const [position, setPosition] = useState(initialPosition || null);

  useEffect(() => {
    setPosition(initialPosition || null);
  }, [initialPosition]);

  useMapEvents({
    click(event) {
      const selected = {
        lat: Number(event.latlng.lat.toFixed(6)),
        lng: Number(event.latlng.lng.toFixed(6)),
      };
      setPosition(selected);
      onSelect?.(selected);
    },
  });

  return position ? <Marker position={position} icon={locationIcon} /> : null;
}

const MapSelectModal = ({
  onClose,
  onSelect,
  initialCenter,
  initialZoom = 10,
  boundaryPolygons = [],
  boundaryLabel = '',
  showSearch = true,
  darkMode = false,
  title = 'Selecionar localização no mapa',
  description = 'Busque uma cidade ou informe as coordenadas para chegar rapidamente ao ponto exato.',
}) => {
  const initialPosition = initialCenter?.lat != null && initialCenter?.lon != null
    ? { lat: Number(initialCenter.lat), lng: Number(initialCenter.lon) }
    : null;
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMode, setSearchMode] = useState('city');
  const [showDropdown, setShowDropdown] = useState(false);
  const hasBoundary = boundaryPolygons.some(polygon => polygon?.length >= 3);
  const [flyToCoords, setFlyToCoords] = useState(hasBoundary ? null : initialPosition);
  const [flyZoom, setFlyZoom] = useState(initialZoom);
  const [searchError, setSearchError] = useState('');
  const [coordinateTerm, setCoordinateTerm] = useState(
    initialPosition ? formatMapCoordinates(initialPosition.lat, initialPosition.lng) : ''
  );
  const [coordinateError, setCoordinateError] = useState('');
  const { municipios, loading, error: municipiosError } = useMunicipiosAutocomplete(searchTerm);

  const selectSearchMode = mode => {
    setSearchMode(mode);
    setShowDropdown(mode === 'city' && searchTerm.trim().length >= 2);
    setSearchError('');
    setCoordinateError('');
  };

  const usarCoordenadas = event => {
    event?.preventDefault?.();
    const parsed = parseMapCoordinates(coordinateTerm);
    if (!parsed.valid) {
      setCoordinateError(parsed.error);
      return;
    }

    setCoordinateError('');
    setCoordinateTerm(parsed.normalized);
    setFlyZoom(18);
    setFlyToCoords({ lat: parsed.lat, lng: parsed.lng });
    onSelect?.({ lat: parsed.lat, lng: parsed.lng });
  };

  const buscarCoordenadas = async (municipio) => {
    const nomeMunicipio = municipio.name_municipio || municipio.name_muni;
    setSearchTerm(nomeMunicipio);
    setShowDropdown(false);
    setSearchError('');

    let query = supabase
      .from('municipios_brazil')
      .select('coordenada_x, coordenada_y')
      .eq('name_muni', nomeMunicipio);

    if (municipio.code_state != null) query = query.eq('code_state', municipio.code_state);

    const { data, error } = await query.limit(1).maybeSingle();
    const lat = Number(data?.coordenada_y);
    const lon = Number(data?.coordenada_x);

    if (error || !Number.isFinite(lat) || !Number.isFinite(lon)) {
      setSearchError('Não foi possível localizar esta cidade. Tente outra opção.');
      return;
    }

    setFlyZoom(12);
    setFlyToCoords({ lat, lng: lon });
  };

  return (
    <div className={`map-select-backdrop ${darkMode ? 'is-dark' : ''}`} role="dialog" aria-modal="true" aria-labelledby="map-select-title">
      <section className="map-select-dialog">
        <header className="map-select-header">
          <div>
            <span>Geolocalização</span>
            <h2 id="map-select-title">{title}</h2>
            <p>{description}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar mapa">×</button>
        </header>

        {showSearch && (
          <div className="map-select-search">
            <div className="map-select-search-modes" role="group" aria-label="Como localizar o ponto">
              <button
                type="button"
                className={searchMode === 'city' ? 'is-active' : ''}
                aria-pressed={searchMode === 'city'}
                onClick={() => selectSearchMode('city')}
              >
                Buscar cidade
              </button>
              <button
                type="button"
                className={searchMode === 'coordinates' ? 'is-active' : ''}
                aria-pressed={searchMode === 'coordinates'}
                onClick={() => selectSearchMode('coordinates')}
              >
                Digitar coordenadas
              </button>
            </div>

            {searchMode === 'city' ? (
              <>
                <label>
                  <span aria-hidden="true">⌕</span>
                  <input
                    type="search"
                    placeholder="Digite a cidade..."
                    value={searchTerm}
                    onChange={(event) => {
                      setSearchTerm(event.target.value);
                      setShowDropdown(event.target.value.trim().length >= 2);
                      setSearchError('');
                    }}
                    onKeyDown={event => {
                      if (event.key === 'Enter' && municipios[0]) {
                        event.preventDefault();
                        buscarCoordenadas(municipios[0]);
                      }
                    }}
                    autoFocus
                    aria-label="Buscar cidade no mapa"
                    autoComplete="off"
                  />
                  {loading && <small>Buscando…</small>}
                </label>

                {showDropdown && municipios.length > 0 && (
                  <div className="map-select-results" role="listbox" aria-label="Cidades encontradas">
                    {municipios.map((municipio, index) => (
                      <button
                        type="button"
                        role="option"
                        aria-selected="false"
                        key={`${municipio.name_muni}-${municipio.code_state}-${index}`}
                        onClick={() => buscarCoordenadas(municipio)}
                      >
                        <strong>{municipio.name_municipio || municipio.name_muni}</strong>
                        <span>{normalizarUf(municipio.sigla_estado || municipio.state || municipio.code_state) || 'Brasil'}</span>
                      </button>
                    ))}
                  </div>
                )}

                {showDropdown && !loading && municipios.length === 0 && searchTerm.trim().length >= 2 && (
                  <p role="status">Nenhuma cidade encontrada. Confira o nome ou use as coordenadas.</p>
                )}
                {(searchError || municipiosError) && <p role="alert">{searchError || 'A busca de cidades está indisponível. Use as coordenadas.'}</p>}
              </>
            ) : (
              <form className="map-select-coordinate-form" onSubmit={usarCoordenadas}>
                <label>
                  <span aria-hidden="true">⌖</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Ex.: -19.245730, -44.633450"
                    value={coordinateTerm}
                    onChange={event => {
                      setCoordinateTerm(event.target.value);
                      setCoordinateError('');
                    }}
                    aria-label="Latitude e longitude do local"
                    autoComplete="off"
                  />
                </label>
                <button type="submit">Usar este ponto</button>
                <small>Cole latitude e longitude separadas por vírgula, espaço ou ponto e vírgula.</small>
                {coordinateError && <p role="alert">{coordinateError}</p>}
              </form>
            )}
          </div>
        )}

        <div className="map-select-canvas">
          <MapContainer
            center={initialPosition ? [initialPosition.lat, initialPosition.lng] : [-15.7801, -47.9292]}
            zoom={initialPosition ? initialZoom : 5}
            zoomAnimation={false}
            fadeAnimation={false}
            style={{ width: '100%', height: '100%' }}
          >
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="Satélite + rótulos">
                <>
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution="Tiles © Esri, Maxar, Earthstar Geographics"
                    maxZoom={20}
                  />
                  <TileLayer
                    url="https://services.arcgisonline.com/arcgis/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                    attribution="Labels © Esri, HERE, Garmin"
                  />
                </>
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Mapa de ruas">
                <TileLayer
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="© OpenStreetMap contributors"
                />
              </LayersControl.BaseLayer>
            </LayersControl>
            <LocationMarker initialPosition={initialPosition} onSelect={onSelect} />
            {boundaryPolygons.map((positions, index) => (
              <Polygon
                key={`map-select-boundary-${index}`}
                positions={positions}
                pathOptions={{
                  color: '#0b8f7a',
                  weight: 3,
                  opacity: 1,
                  fillColor: '#29b89e',
                  fillOpacity: 0.16,
                  dashArray: '9 7',
                }}
              />
            ))}
            {hasBoundary && <FitBoundary polygons={boundaryPolygons} />}
            {flyToCoords && <FlyToLocation lat={flyToCoords.lat} lon={flyToCoords.lng ?? flyToCoords.lon} zoom={flyZoom} />}
          </MapContainer>
          {hasBoundary && (
            <div className="map-select-boundary-label" role="status">
              <span aria-hidden="true">▱</span>
              <div>
                <strong>Limites da propriedade</strong>
                {boundaryLabel && <small>{boundaryLabel}</small>}
              </div>
            </div>
          )}
        </div>

        <div className="map-select-guidance">
          <span aria-hidden="true">📍</span>
          <div><strong>Marque o ponto exato</strong><small>Toque no mapa ou use uma coordenada pronta. O ponto escolhido preencherá latitude e longitude automaticamente.</small></div>
        </div>
      </section>
    </div>
  );
};

export default MapSelectModal;
